import { ref, onMounted, onUnmounted, type Ref } from 'vue';
import type { StatusSnapshot } from '@/types';
import { useConfig } from './useConfig';

interface UseStatusReturn {
  snapshot: Ref<StatusSnapshot | null>;
  loading: Ref<boolean>;
  error: Ref<Error | null>;
  lastUpdated: Ref<Date | null>;
  refresh: () => Promise<void>;
}

export function useStatus(): UseStatusReturn {
  const config = useConfig();
  const snapshot: Ref<StatusSnapshot | null> = ref(null);
  const loading = ref(false);
  const error: Ref<Error | null> = ref(null);
  const lastUpdated: Ref<Date | null> = ref(null);

  let intervalId: ReturnType<typeof setInterval> | null = null;

  async function refresh(): Promise<void> {
    loading.value = true;
    error.value = null;
    try {
      const response = await fetch(config.value.statusUrl, { cache: 'no-store' });
      if (!response.ok) {
        throw new Error(`Status fetch failed: ${response.status}`);
      }
      snapshot.value = (await response.json()) as StatusSnapshot;
      lastUpdated.value = new Date();
    } catch (err) {
      error.value = err instanceof Error ? err : new Error('Unknown error');
    } finally {
      loading.value = false;
    }
  }

  onMounted(() => {
    void refresh();
    intervalId = setInterval(() => void refresh(), config.value.refreshIntervalMs);
  });

  onUnmounted(() => {
    if (intervalId !== null) clearInterval(intervalId);
  });

  return { snapshot, loading, error, lastUpdated, refresh };
}
